<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <h1 class="text-h4 mb-4">{{ $t('roles.title') }}</h1>
          <v-btn color="primary" @click="openAddDialog">{{ $t('roles.add') }}</v-btn>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>{{ $t('roles.list') }}</v-card-title>
          <v-card-text>
            <v-table density="compact">
              <thead>
                <tr>
                  <th>{{ $t('roles.id') }}</th>
                  <th>{{ $t('roles.name') }}</th>
                  <th>{{ $t('roles.description') }}</th>
                  <th>{{ $t('roles.permissions') }}</th>
                  <th>{{ $t('roles.users') }}</th>
                  <th>{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="role in roles" :key="role.id">
                  <td>{{ role.id }}</td>
                  <td><strong>{{ role.name }}</strong></td>
                  <td>{{ role.description || '-' }}</td>
                  <td>{{ role.permissions_count }}</td>
                  <td>{{ role.users_count }}</td>
                  <td>
                    <v-btn variant="plain" size="small" @click="editRole(role)" icon="mdi-pencil"></v-btn>
                    <v-btn variant="plain" size="small" @click="managePermissions(role)" icon="mdi-shield-account"></v-btn>
                    <v-btn variant="plain" size="small" color="error" @click="deleteRole(role.id)" icon="mdi-delete"></v-btn>
                  </td>
                </tr>
                <tr v-if="roles.length === 0">
                  <td colspan="6" class="text-center text-grey">{{ $t('roles.noRolesFound') }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showAddDialog" max-width="600" @update:model-value="(val) => { if (!val) resetForm(); }">
      <v-card>
        <v-card-title>{{ roleForm.id ? $t('roles.edit') : $t('roles.add') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field
              v-model="roleForm.name"
              :label="$t('roles.name')"
              required
              density="compact"
              :rules="[v => !!v || $t('roles.nameRequired')]"
            />
            <v-textarea
              v-model="roleForm.description"
              :label="$t('roles.description')"
              rows="3"
              density="compact"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showAddDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveRole">{{ roleForm.id ? $t('common.save') : $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPermissionsDialog" max-width="800">
      <v-card>
        <v-card-title>{{ $t('roles.managePermissions', { name: selectedRole?.name }) }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="selectedPermissions"
            :items="allPermissions"
            item-title="name"
            item-value="id"
            :label="$t('roles.permissions')"
            multiple
            chips
            density="compact"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showPermissionsDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveRolePermissions">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

const { t } = useI18n()

interface Permission {
  id: number
  name: string
  controller: string
  action: string | null
  description: string | null
}

interface Role {
  id: number
  name: string
  description: string | null
  permissions_count: number
  users_count: number
}

const roles = ref<Role[]>([])
const allPermissions = ref<Permission[]>([])
const showAddDialog = ref(false)
const showPermissionsDialog = ref(false)
const selectedRole = ref<Role | null>(null)
const selectedPermissions = ref<number[]>([])
const roleForm = ref({
  id: null as number | null,
  name: '',
  description: ''
})

const loadRoles = async () => {
  try {
    const response = await apiGet('/api/roles')
    if (response.ok) {
      roles.value = await response.json()
    }
  } catch (error) {
    console.error('Load roles error:', error)
  }
}

const loadPermissions = async () => {
  try {
    const response = await apiGet('/api/permissions')
    if (response.ok) {
      allPermissions.value = await response.json()
    }
  } catch (error) {
    console.error('Load permissions error:', error)
  }
}

const openAddDialog = () => {
  resetForm()
  showAddDialog.value = true
}

const editRole = (role: Role) => {
  roleForm.value = {
    id: role.id,
    name: role.name,
    description: role.description || ''
  }
  showAddDialog.value = true
}

const managePermissions = async (role: Role) => {
  selectedRole.value = role
  try {
    const response = await apiGet(`/api/roles/${role.id}`)
    if (response.ok) {
      const roleData = await response.json()
      selectedPermissions.value = roleData.permissions.map((p: Permission) => p.id)
      showPermissionsDialog.value = true
    }
  } catch (error) {
    console.error('Load role permissions error:', error)
  }
}

const saveRole = async () => {
  try {
    if (roleForm.value.id) {
      const response = await apiPut(`/api/roles/${roleForm.value.id}`, {
        role: roleForm.value
      })
      if (response.ok) {
        await loadRoles()
        showAddDialog.value = false
      } else {
        const data = await response.json()
        alert(data.errors?.join(', ') || t('roles.failedToSave'))
      }
    } else {
      const response = await apiPost('/api/roles', {
        role: roleForm.value
      })
      if (response.ok) {
        await loadRoles()
        showAddDialog.value = false
      } else {
        const data = await response.json()
        alert(data.errors?.join(', ') || t('roles.failedToCreate'))
      }
    }
  } catch (error) {
    console.error('Save role error:', error)
    alert(t('roles.failedToSave'))
  }
}

const saveRolePermissions = async () => {
  if (!selectedRole.value) return

  try {
    const response = await apiPut(`/api/roles/${selectedRole.value.id}/assign_permissions`, {
      permission_ids: selectedPermissions.value
    })
    if (response.ok) {
      await loadRoles()
      showPermissionsDialog.value = false
    } else {
      alert(t('roles.failedToUpdatePermissions'))
    }
  } catch (error) {
    console.error('Save role permissions error:', error)
    alert(t('roles.failedToUpdatePermissions'))
  }
}

const deleteRole = async (id: number) => {
  if (!confirm(t('roles.areYouSureDelete'))) return

  try {
    const response = await apiDelete(`/api/roles/${id}`)
    if (response.ok) {
      await loadRoles()
    } else {
      alert(t('roles.failedToDelete'))
    }
  } catch (error) {
    console.error('Delete role error:', error)
    alert(t('roles.failedToDelete'))
  }
}

const resetForm = () => {
  roleForm.value = {
    id: null,
    name: '',
    description: ''
  }
}

onMounted(() => {
  loadRoles()
  loadPermissions()
})
</script>

